import { lazy, ReactNode, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { BottomNav } from './components/BottomNav';
import { ImpersonateBanner } from './components/ImpersonateBanner';
import { Sidebar } from './components/Sidebar';
import { UpdatePrompt } from './components/UpdatePrompt';
import { AuthProvider, useAuth } from './lib/auth';
// Sprint 12 · S12.1 · Hot-Path-Routes (employee/dispatcher Daily-Use) bleiben static.
import { Capture } from './routes/Capture';
import { Dashboard } from './routes/Dashboard';
import { LeadDetail } from './routes/LeadDetail';
import { Leads } from './routes/Leads';
import { defaultRouteForRole, Login } from './routes/Login';
import { PasswordResetConfirm } from './routes/PasswordResetConfirm';
import { PasswordResetRequest } from './routes/PasswordResetRequest';
import { Profile } from './routes/Profile';
import { Protected } from './routes/Protected';
import { SetupPassword } from './routes/SetupPassword';
import { Users } from './routes/Users';

// Sprint 7: FormBuilder lazy laden -> @dnd-kit-Bundle (~60 KB) bleibt aus dem PWA-Precache.
const FormBuilder = lazy(() =>
  import('./routes/FormBuilder').then((m) => ({ default: m.FormBuilder })),
);

// Sprint 12 · S12.1 · Admin/Settings-Routes lazy -> Code lädt erst, wenn Admin
// auf seine Page geht. Senkt initial-Bundle für employee-Daily-Use.
const AdminAiUsage = lazy(() =>
  import('./routes/AdminAiUsage').then((m) => ({ default: m.AdminAiUsage })),
);
const AdminHealth = lazy(() =>
  import('./routes/AdminHealth').then((m) => ({ default: m.AdminHealth })),
);
const AdminTenants = lazy(() =>
  import('./routes/AdminTenants').then((m) => ({ default: m.AdminTenants })),
);
const AdminTenantDetail = lazy(() =>
  import('./routes/AdminTenantDetail').then((m) => ({ default: m.AdminTenantDetail })),
);
const SettingsMail = lazy(() =>
  import('./routes/SettingsMail').then((m) => ({ default: m.SettingsMail })),
);
const SettingsMailTemplates = lazy(() =>
  import('./routes/SettingsMailTemplates').then((m) => ({ default: m.SettingsMailTemplates })),
);
const SettingsAiPrompts = lazy(() =>
  import('./routes/SettingsAiPrompts').then((m) => ({ default: m.SettingsAiPrompts })),
);
const SettingsBranding = lazy(() =>
  import('./routes/SettingsBranding').then((m) => ({ default: m.SettingsBranding })),
);
// Sprint 12 · Dev-Routes ebenfalls lazy (selten gebraucht).
const DevMagicDiff = lazy(() =>
  import('./routes/DevMagicDiff').then((m) => ({ default: m.DevMagicDiff })),
);
const DevMailEditor = lazy(() =>
  import('./routes/DevMailEditor').then((m) => ({ default: m.DevMailEditor })),
);

// Sprint 12 · S12.1 · einheitlicher Suspense-Fallback (Token-konform, keine Layout-Shift).
function RouteFallback() {
  return <div className="auth-page">Lädt…</div>;
}

/**
 * Sprint 16 · S16.2 · App-Layout:
 * Sidebar links (Desktop ≥ 768px, nur für admins) + Main-Content rechts.
 * Auf Mobile / non-admin: kein Padding-Left (Sidebar ist hidden).
 */
function AppShell({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const showSidebar =
    !!user && (user.role === 'operator_admin' || user.role === 'super_admin');
  return (
    <>
      <Sidebar />
      <BottomNav />
      <div className={`lc-layout ${showSidebar ? 'lc-layout--with-sidebar' : ''}`}>
        {children}
      </div>
    </>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="auth-page">Lädt…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_reset_pw) return <Navigate to="/setup-password" replace />;
  return <Navigate to={defaultRouteForRole(user.role)} replace />;
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <UpdatePrompt />
        <ImpersonateBanner />
        <AppShell>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<PasswordResetRequest />} />
          <Route path="/reset-password/confirm" element={<PasswordResetConfirm />} />
          <Route
            path="/setup-password"
            element={
              <Protected>
                <SetupPassword />
              </Protected>
            }
          />
          <Route
            path="/dashboard"
            element={
              <Protected>
                <Dashboard />
              </Protected>
            }
          />
          <Route
            path="/users"
            element={
              <Protected roles={['super_admin', 'operator_admin', 'dispatcher', 'employee']}>
                <Users />
              </Protected>
            }
          />
          <Route
            path="/capture"
            element={
              <Protected roles={['employee', 'dispatcher', 'operator_admin']}>
                <Capture />
              </Protected>
            }
          />
          <Route
            path="/leads"
            element={
              <Protected roles={['super_admin', 'operator_admin', 'dispatcher', 'employee']}>
                <Leads />
              </Protected>
            }
          />
          <Route
            path="/leads/:id"
            element={
              <Protected roles={['super_admin', 'operator_admin', 'dispatcher', 'employee']}>
                <LeadDetail />
              </Protected>
            }
          />
          <Route
            path="/profile"
            element={
              <Protected>
                <Profile />
              </Protected>
            }
          />
          <Route
            path="/settings/mail"
            element={
              <Protected roles={['super_admin', 'operator_admin']}>
                <Suspense fallback={<RouteFallback />}>
                  <SettingsMail />
                </Suspense>
              </Protected>
            }
          />
          <Route
            path="/settings/mail-templates"
            element={
              <Protected roles={['super_admin', 'operator_admin']}>
                <Suspense fallback={<RouteFallback />}>
                  <SettingsMailTemplates />
                </Suspense>
              </Protected>
            }
          />
          {/* Sprint 64 · KI-Prompts pro Tenant */}
          <Route
            path="/settings/ai-prompts"
            element={
              <Protected roles={['super_admin', 'operator_admin']}>
                <Suspense fallback={<RouteFallback />}>
                  <SettingsAiPrompts />
                </Suspense>
              </Protected>
            }
          />
          <Route
            path="/settings/branding"
            element={
              <Protected roles={['super_admin', 'operator_admin']}>
                <Suspense fallback={<RouteFallback />}>
                  <SettingsBranding />
                </Suspense>
              </Protected>
            }
          />
          <Route
            path="/admin/forms"
            element={
              <Protected roles={['super_admin', 'operator_admin']}>
                <Suspense fallback={<RouteFallback />}>
                  <FormBuilder />
                </Suspense>
              </Protected>
            }
          />
          <Route
            path="/admin/tenants"
            element={
              <Protected roles={['super_admin']}>
                <Suspense fallback={<RouteFallback />}>
                  <AdminTenants />
                </Suspense>
              </Protected>
            }
          />
          <Route
            path="/admin/tenants/:id"
            element={
              <Protected roles={['super_admin']}>
                <Suspense fallback={<RouteFallback />}>
                  <AdminTenantDetail />
                </Suspense>
              </Protected>
            }
          />
          <Route
            path="/admin/health"
            element={
              <Protected roles={['super_admin']}>
                <Suspense fallback={<RouteFallback />}>
                  <AdminHealth />
                </Suspense>
              </Protected>
            }
          />
          <Route
            path="/admin/ai-usage"
            element={
              <Protected roles={['super_admin']}>
                <Suspense fallback={<RouteFallback />}>
                  <AdminAiUsage />
                </Suspense>
              </Protected>
            }
          />
          <Route
            path="/dev/magic-diff"
            element={
              <Protected>
                <Suspense fallback={<RouteFallback />}>
                  <DevMagicDiff />
                </Suspense>
              </Protected>
            }
          />
          <Route
            path="/dev/mail-editor"
            element={
              <Protected>
                <Suspense fallback={<RouteFallback />}>
                  <DevMailEditor />
                </Suspense>
              </Protected>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </AppShell>
      </BrowserRouter>
    </AuthProvider>
  );
}
